import type { AuthUser, RegisterData, LoginData, GoogleLoginData, AppleLoginData } from '@/lib/types/auth';

const API_URL = '/api/auth';

type AuthEnvelope = {
  user?: Partial<AuthUser>;
  data?: Partial<AuthUser> | { user?: Partial<AuthUser>; token?: string; access_token?: string };
  token?: string;
  access_token?: string;
};

type AuthResponse = AuthUser | AuthEnvelope;

function resolveAvatarUrl(raw: string | undefined): string {
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!raw.startsWith('/')) return raw;
  const apiBase = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${raw}`;
}

function normalizeAuthResponse(data: AuthResponse): AuthUser {
  const envelope = data as AuthEnvelope;
  const dataValue = envelope.data as AuthEnvelope | Partial<AuthUser> | undefined;
  const resolvedUser = ((dataValue && 'user' in dataValue ? dataValue.user : undefined) ||
    envelope.user ||
    (dataValue && !('user' in dataValue) ? dataValue : undefined) ||
    (data as Partial<AuthUser>)) as Partial<AuthUser>;

  const resolvedToken =
    envelope.token ||
    envelope.access_token ||
    (dataValue && 'token' in dataValue ? dataValue.token : undefined) ||
    (dataValue && 'access_token' in dataValue ? dataValue.access_token : undefined) ||
    '';

  const avatarRaw = resolvedUser?.avatar || (resolvedUser as any)?.profile_photo_url || '';

  return {
    id: String(resolvedUser?.id || ''),
    name: resolvedUser?.name || '',
    email: resolvedUser?.email || '',
    avatar: resolveAvatarUrl(avatarRaw),
    pmi: resolvedUser?.pmi,
    role: resolvedUser?.role,
    token: resolvedToken || resolvedUser?.token || '',
  };
}

async function parseResponse<T>(res: Response, fallbackMessage: string): Promise<T> {
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
    throw new Error(errors || data?.message || fallbackMessage);
  }

  return data;
}

export async function register(data: RegisterData): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  return normalizeAuthResponse(await parseResponse<AuthResponse>(res, 'Registration failed'));
}

export async function login(data: LoginData): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  return normalizeAuthResponse(await parseResponse<AuthResponse>(res, 'Login failed'));
}

export async function getMe(token: string): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await parseResponse<AuthResponse>(res, 'Failed to fetch user');
  return normalizeAuthResponse('user' in data ? data : { ...data, token });
}

export async function logout(token: string): Promise<void> {
  const res = await fetch(`${API_URL}/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  await parseResponse(res, 'Logout failed');
}

export async function googleLogin(data: GoogleLoginData): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  return normalizeAuthResponse(await parseResponse<AuthResponse>(res, 'Google login failed'));
}

export async function appleLogin(data: AppleLoginData): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  return normalizeAuthResponse(await parseResponse<AuthResponse>(res, 'Apple login failed'));
}

export async function updateProfile(data: { name?: string; avatar?: File | null }, token?: string): Promise<AuthUser> {
  const form = new FormData();
  if (data.name !== undefined) form.append('name', data.name);
  if (data.avatar) form.append('profile_photo', data.avatar);

  const res = await fetch(`${API_URL}/profile`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  });

  return normalizeAuthResponse(await parseResponse<AuthResponse>(res, 'Failed to update profile'));
}

// ── Ajukan hapus akun (dieksekusi setelah disetujui admin) ──────────────────
export type DeletionRequest = {
  id: number;
  status: 'pending' | 'approved' | 'rejected';
  reason: string;
  admin_note: string | null;
  reviewed_at: string | null;
  created_at: string;
};

export async function requestAccountDeletion(
  data: { name: string; email: string; reason: string },
  token: string,
): Promise<{ message: string; request: DeletionRequest }> {
  const res = await fetch(`${API_URL}/account-deletion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });

  return parseResponse<{ message: string; request: DeletionRequest }>(res, 'Gagal mengajukan hapus akun');
}

export async function getAccountDeletionStatus(token: string): Promise<{ request: DeletionRequest | null }> {
  const res = await fetch(`${API_URL}/account-deletion`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return parseResponse<{ request: DeletionRequest | null }>(res, 'Gagal memuat status permintaan');
}

export async function cancelAccountDeletion(token: string): Promise<{ message: string }> {
  const res = await fetch(`${API_URL}/account-deletion`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  return parseResponse<{ message: string }>(res, 'Gagal membatalkan permintaan');
}

// ── Lupa password via kode OTP ──────────────────────────────────────────────
export async function sendResetOtp(email: string): Promise<{ message: string }> {
  const res = await fetch(`${API_URL}/password/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  return parseResponse<{ message: string }>(res, 'Gagal mengirim kode');
}

export async function resetPasswordWithOtp(data: {
  email: string;
  otp: string;
  password: string;
  password_confirmation: string;
}): Promise<{ message: string }> {
  const res = await fetch(`${API_URL}/password/otp/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  return parseResponse<{ message: string }>(res, 'Gagal reset password');
}
